import React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import GoogleIcon from '../../../shared/components/icons/GoogleIcon';
import useTowns from '../../jobs/hooks/useTowns';
import Button from '../../../shared/components/ui/Button';
import { signupUser } from '../../../services/api';
import { setAuth } from '../utils/authStorage';
import TownAutocomplete from '../../../shared/components/inputs/TownAutocomplete';
import CardIcon from '../../../shared/components/icons/CardIcon';

function SignupStep({ number, icon, title, description }) {
  return (
    <div className="flex items-start gap-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-orange-100 text-orange-700">
        <CardIcon name={icon} className="h-4 w-4" />
      </span>
      <div>
        <p className="text-xs font-bold uppercase text-orange-700">Step {number}</p>
        <h2 className="mt-0.5 text-base font-bold text-navy">{title}</h2>
        {description && <p className="mt-1 text-sm leading-5 text-slate-600">{description}</p>}
      </div>
    </div>
  );
}

function SignupPage() {
  const navigate = useNavigate();
  const { towns } = useTowns();
  const [form, setForm] = React.useState({
    firstName: '',
    middleName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    role: 'WORKER',
    city: '',
    area: '',
  });
  const [showModal, setShowModal] = React.useState(false);

  const onChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const setZambiaPhone = (national) => {
    const digits = String(national || '').replace(/[^\d]/g, '');
    setForm((prev) => ({ ...prev, phone: digits ? `+260${digits}` : '' }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const required = ['firstName', 'lastName', 'phone', 'password', 'role', 'city', 'area'];
    const missing = required.filter((k) => !String(form[k] || '').trim());
    if (missing.length > 0) {
      toast.error('Please fill in all required fields.');
      return;
    }

    try {
      const data = await signupUser(form);
      if (data?.token && data?.user) {
        setAuth({ token: data.token, user: data.user });
      }
      toast.success(data?.message || 'Sign up successful');
      setShowModal(true);
    } catch (error) {
      toast.error(error?.message || 'Sign up failed');
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/40">
        <header className="border-b border-slate-100 bg-gradient-to-r from-amber-50 via-white to-orange-50 px-6 py-7 sm:px-10">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Tuma Jobs" className="h-20 w-20 shrink-0 object-contain" />
            <div>
              <p className="text-xs font-bold uppercase text-orange-700">Yours. Mine. Ours.</p>
              <h1 className="mt-2 text-3xl font-bold text-navy">Join Tuma Jobs</h1>
              <p className="mt-2 text-sm text-slate-600">Create your account and get started.</p>
            </div>
          </div>
        </header>

        <form onSubmit={onSubmit} className="space-y-8 p-6 sm:p-10">
          <section className="space-y-4">
            <SignupStep number="1" icon="briefcase" title="What would you like to do?" description="Choose your primary role." />
            <fieldset className="grid gap-3 sm:grid-cols-2">
              <legend className="sr-only">Choose your primary role</legend>
              <label className={`cursor-pointer rounded-2xl border p-4 transition focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-200 ${form.role === 'WORKER' ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200' : 'border-slate-200 bg-white hover:border-orange-300'}`}>
                <input type="radio" name="role" value="WORKER" checked={form.role === 'WORKER'} onChange={onChange('role')} className="sr-only accent-orange-600" />
                <span className="flex items-start gap-3"><CardIcon name="search" className="mt-0.5 h-5 w-5 shrink-0 text-orange-700" /><span><span className="block text-sm font-semibold text-slate-900">Find Work</span><span className="mt-1 block text-xs leading-5 text-slate-600">Find jobs and apply for opportunities.</span></span></span>
              </label>
              <label className={`cursor-pointer rounded-2xl border p-4 transition focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-200 ${form.role === 'CLIENT' ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200' : 'border-slate-200 bg-white hover:border-orange-300'}`}>
                <input type="radio" name="role" value="CLIENT" checked={form.role === 'CLIENT'} onChange={onChange('role')} className="sr-only accent-orange-600" />
                <span className="flex items-start gap-3"><CardIcon name="people" className="mt-0.5 h-5 w-5 shrink-0 text-orange-700" /><span><span className="block text-sm font-semibold text-slate-900">Hire Workers</span><span className="mt-1 block text-xs leading-5 text-slate-600">Find people for your jobs.</span></span></span>
              </label>
            </fieldset>
          </section>

          <section className="space-y-4 border-t border-slate-100 pt-7">
            <SignupStep number="2" icon="user" title="Your details" />
            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-700">Full name</p>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label htmlFor="signup-first-name" className="mb-2 block text-sm font-medium text-slate-700">First name</label>
                  <input id="signup-first-name" value={form.firstName} onChange={onChange('firstName')} placeholder="First name" autoComplete="given-name" required className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100" />
                </div>
                <div>
                  <label htmlFor="signup-middle-name" className="mb-2 block text-sm font-medium text-slate-700">Middle name <span className="font-normal text-slate-400">(optional)</span></label>
                  <input id="signup-middle-name" value={form.middleName} onChange={onChange('middleName')} placeholder="Middle name" autoComplete="additional-name" className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100" />
                </div>
                <div>
                  <label htmlFor="signup-last-name" className="mb-2 block text-sm font-medium text-slate-700">Last name</label>
                  <input id="signup-last-name" value={form.lastName} onChange={onChange('lastName')} placeholder="Last name" autoComplete="family-name" required className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100" />
                </div>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="signup-phone" className="mb-2 block text-sm font-medium text-slate-700">Phone number</label>
                <div className="flex">
                  <span className="inline-flex items-center gap-1.5 rounded-l-xl border border-slate-300 bg-slate-50 px-3 text-sm font-semibold text-slate-700"><CardIcon name="phone" className="h-4 w-4" />+260</span>
                  <input id="signup-phone" value={String(form.phone || '').startsWith('+260') ? String(form.phone).slice(4) : form.phone} onChange={(event) => setZambiaPhone(event.target.value)} placeholder="97 XXX XXXX" inputMode="numeric" autoComplete="tel-national" required className="w-full min-w-0 rounded-r-xl border border-l-0 border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100" />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="signup-email" className="mb-2 block text-sm font-medium text-slate-700">Email address <span className="font-normal text-slate-400">(optional if phone is sufficient for login)</span></label>
                <input id="signup-email" value={form.email} onChange={onChange('email')} type="email" autoComplete="email" placeholder="you@example.com" className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="signup-password" className="mb-2 block text-sm font-medium text-slate-700">Password</label>
                <input id="signup-password" value={form.password} onChange={onChange('password')} type="password" autoComplete="new-password" placeholder="Create a strong password" required className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100" />
              </div>
            </div>
          </section>

          <section className="space-y-4 border-t border-slate-100 pt-7">
            <SignupStep number="3" icon="location" title="Where are you located?" description="Your location helps us connect you with relevant opportunities." />
            <div className="grid gap-4 sm:grid-cols-2">
              <TownAutocomplete id="signup-city" label="City / town" value={form.city} onChange={(city) => setForm((prev) => ({ ...prev, city }))} towns={towns} required placeholder="e.g. Chitambo" />
              <div>
                <label htmlFor="signup-area" className="mb-2 block text-sm font-medium text-slate-700">Area / community</label>
                <input id="signup-area" value={form.area} onChange={onChange('area')} placeholder="e.g. Mukando" autoComplete="address-level3" required className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100" />
              </div>
            </div>
            <p className="text-xs leading-5 text-slate-500">Your contact information should only be shared according to Tuma Jobs&apos; privacy policy.</p>
          </section>

          <div className="border-t border-slate-100 pt-6">
            <Button type="submit" variant="primary" className="w-full">Create account</Button>
            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs font-semibold text-slate-500">OR</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <button type="button" onClick={() => toast.info('Google sign up will be added later.')} className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-800 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-orange focus:ring-offset-2">
              <GoogleIcon className="h-5 w-5" />Continue with Google
            </button>
            <p className="mt-5 text-center text-xs leading-5 text-slate-500">By creating an account, you agree to our Terms of Service and Privacy Policy.</p>
            <p className="mt-5 text-center text-sm text-slate-600">Already have an account? <Link to="/login" className="font-semibold text-navy hover:underline">Log in</Link></p>
          </div>
        </form>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-xl">
            <h2 className="text-xl font-bold text-navy">Welcome to Tuma Jobs!</h2>
            <p className="mt-2 text-sm text-slate-600">
              Your account has been created successfully. You can now start exploring jobs or posting them.
            </p>
            <div className="mt-6">
              <Button
                onClick={() => {
                  setShowModal(false);
                  const role = form.role;
                  if (role === 'CLIENT') navigate('/client', { replace: true });
                  else navigate('/worker', { replace: true });
                }}
                variant="primary"
                className="w-full"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SignupPage;

